import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Avatar,
  AvatarGroup,
  Box,
  Card,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import sampleProfileImage from "assets/images/sample.png";
import MDBox from "components/MDBox";
import { getAPICall } from "lib/callApi";
import { FETCH_SINGLE_KPI_TEMPLATE } from "lib/constants";
import TooltipLight from "utils/TooltipLight";
import React, { useState } from "react";
import KpiDetailsPopover from "./KpiDetailsPopover";

function KPITile({ template, selectedTemplateId, setSelectedTemplateId }) {
  const iconPath = "/logos/alysio_icon_black.svg";
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [popoverContent, setPopoverContent] = useState("");
  const [popoverAnchor, setPopoverAnchor] = useState(null);

  const getAssignedUserDisplay = () =>
    template.Users.length ? `${template.Users.length} Assigned` : "Unassigned";

  const renderKPIInfo = (activitiesWithPoints) => (
    <KpiDetailsPopover activitiesWithPoints={activitiesWithPoints} />
  );

  const fetchPopoverContent = async () => {
    try {
      if (!popoverContent) {
        const result = await getAPICall(`${FETCH_SINGLE_KPI_TEMPLATE}/${template.id}`, true);
        if (result?.data?.success) {
          const { activitiesWithPoints } = result.data.data;
          setPopoverContent(renderKPIInfo(activitiesWithPoints));
        } else {
          setPopoverContent("Failed to load KPIs");
        }
      }
    } catch (error) {
      setPopoverContent("Failed to load KPIs");
    }
  };

  const handlePopoverOpen = async (event) => {
    setPopoverAnchor(event.currentTarget);
    await fetchPopoverContent();
    setPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setPopoverOpen(false);
  };

  const renderTooltip = () => (
    <List sx={{ maxHeight: "450px", overflow: "auto" }}>
      {template.Users.map((user, index) => (
        <React.Fragment key={`user-tooltip-${user.id}`}>
          <ListItem>
            <ListItemAvatar sx={{ "& .MuiAvatar-root": { width: 28, height: 28, fontSize: 15 } }}>
              <Avatar
                variant="circular"
                alt={user.name.toUpperCase()}
                src={user?.profilePicture || sampleProfileImage}
              />
            </ListItemAvatar>
            <ListItemText
              primary={
                <Typography component="h5" variant="body2" color="#888">
                  {user.name}
                </Typography>
              }
            />
          </ListItem>
          {index < template.Users.length - 1 && <Divider component="li" sx={{ my: "0.4rem" }} />}
        </React.Fragment>
      ))}
    </List>
  );

  return (
    <Grid item xs={12} sm={12} lg={6} md={6} key={template.id}>
      <Card
        variant="outlined"
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "1rem",
          cursor: "pointer",
          outline:
            selectedTemplateId === template.id ? "3px solid cornflowerblue" : "1px solid #aaa",
          boxShadow:
            selectedTemplateId === template.id
              ? "0 0 1rem 0.2rem rgb(100 150 238 / 30%)"
              : "0rem 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1),0rem 0.125rem 0.25rem -0.0625rem rgba(0, 0, 0, 0.06)",
        }}
        onClick={() => setSelectedTemplateId(template.id)}
      >
        <Stack direction="row" justifyContent="space-between" height="35px">
          <Box display="flex" flexGrow={1} overflow="hidden">
            {template.status === "alysio_template" && (
              <Box
                component="img"
                sx={{
                  width: 30,
                  height: 30,
                  display: "inline-block",
                  verticalAlign: "middle",
                  mr: "0.5rem",
                }}
                src={iconPath}
                alt="Alysio logo"
              />
            )}
            <Typography
              variant="h5"
              component="h5"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100%",
              }}
            >
              {template.name}
            </Typography>
          </Box>
          <Box>
            <Stack
              direction="column"
              width="50px"
              alignItems="center"
              justifyContent="center"
              onClick={handlePopoverOpen}
              sx={{ "& :hover": { cursor: "pointer" } }}
            >
              <IconButton sx={{ p: 0 }} disableRipple>
                <VisibilityIcon
                  sx={{
                    height: "20px",
                    width: "20px",
                    color: "#344767d9",
                  }}
                />
              </IconButton>
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "#344767d9",
                  lineHeight: "0.7rem",
                }}
              >
                View KPIs
              </Typography>
            </Stack>
          </Box>
        </Stack>
        <MDBox
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            height: "20px",
          }}
        >
          <Typography
            variant="caption"
            component="h6"
            style={{ color: "rgba(0, 0, 0, 0.87)" }}
            sx={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
          >
            {template.description}
          </Typography>
        </MDBox>
        <Stack
          direction="row"
          sx={{
            alignItems: "flex-end",
            height: "30px",
            justifyContent: "space-between",
            mt: "0.35rem",
          }}
        >
          <Typography sx={{ fontSize: "0.8rem", color: "#818da2" }}>
            {getAssignedUserDisplay()}
          </Typography>
          {template.Users.length > 0 && (
            <TooltipLight title={renderTooltip()} sx={{ minWidth: "150px" }} placement="right">
              <AvatarGroup
                max={4}
                sx={{
                  "& .MuiAvatar-root": { width: 28, height: 28, fontSize: 15 },
                  "& .MuiAvatar-colorDefault": {
                    backgroundColor: "#fff",
                    color: "#818da2",
                    borderColor: "#fff",
                    fontWeight: "600",
                    pl: "0.25rem",
                  },
                }}
              >
                {template.Users.map((user) => (
                  <Avatar
                    key={`avatar-${user.id}`}
                    alt={user.name.toUpperCase()}
                    src={user.profilePicture || sampleProfileImage}
                  >
                    {user.name.slice(0, 1)}
                  </Avatar>
                ))}
              </AvatarGroup>
            </TooltipLight>
          )}
        </Stack>
        <Popover
          open={popoverOpen}
          anchorEl={popoverAnchor}
          onClose={handlePopoverClose}
          anchorOrigin={{
            horizontal: "right",
          }}
          sx={{
            "& .MuiPopover-paper": {
              p: 0,
            },
          }}
        >
          {popoverContent}
        </Popover>
      </Card>
    </Grid>
  );
}

export default KPITile;
